import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { districts } from './Districts'; // Import district coordinates
import {
  getDeliveryPricingInfo,
  hasDeliveryAddons,
  isSpecialTariffDelivery,
} from './pricingIntelligence';
import { getDeliverySlotLabel } from '../../../utils/deliverySlots';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

function matchesDeliveryMarkerFilter(deliveryPoint, filter) {
  if (!filter || filter === 'all') return true;
  if (filter === 'special-tariff') return isSpecialTariffDelivery(deliveryPoint);
  if (filter === 'with-addons') return hasDeliveryAddons(deliveryPoint);
  return deliveryPoint.status === filter;
}

function escapeMarkerText(value) {
  return String(value).replace(/[&<>"']/g, (character) => ({
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#39;',
  }[character]));
}

function ControlTowerMap({
  deliveryPoints,
  orders,
  drivers,
  selectedDriverId,
  showDeliveryMarkers,
  showOrderMarkers,
  isDrawingMode,
  onDrawingComplete,
  onMarkerClick,
  deliveryMarkerFilter = 'all',
  sidebarState,
}) {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersLayerRef = useRef(null);
  const driversLayerRef = useRef(null);
  const rectangleLayerRef = useRef(null);
  
  // Rectangle drawing state
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState(null);
  const [currentRectangle, setCurrentRectangle] = useState(null);

  // Initialize map
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    console.log('🗺️ Initializing map...');

    // Create map centered on Lima, Peru
    const map = L.map(mapRef.current, {
      zoomControl: true
    }).setView([-12.0464, -77.0428], 12);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(map);

    // Create layers
    markersLayerRef.current = L.layerGroup().addTo(map);
    driversLayerRef.current = L.layerGroup().addTo(map);
    rectangleLayerRef.current = L.layerGroup().addTo(map);

    mapInstanceRef.current = map;

    console.log('✅ Map initialized');

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const map = mapInstanceRef.current;

    // Wait for CSS transition (duration-300) to finish
    const timeoutId = setTimeout(() => {
      console.log('📐 Invalidating map size after sidebar change');
      map.invalidateSize();
    }, 350);

    return () => clearTimeout(timeoutId);
  }, [sidebarState]);

  // Handle drawing mode with native Leaflet
  useEffect(() => {
    if (!mapInstanceRef.current) return;

    const map = mapInstanceRef.current;

    if (isDrawingMode) {
      // Disable map dragging when in drawing mode
      map.dragging.disable();
      
      // Change cursor
      map.getContainer().style.cursor = 'crosshair';

      // Prevent context menu (right-click menu)
      const preventContextMenu = (e) => {
        e.preventDefault();
        return false;
      };
      map.getContainer().addEventListener('contextmenu', preventContextMenu);

      // Mouse down - start drawing (LEFT CLICK ONLY)
      const onMouseDown = (e) => {
        // Only respond to left click (button 0)
        if (e.originalEvent.button !== 0) return;
        
        e.originalEvent.preventDefault(); // Prevent default behavior
        setIsDrawing(true);
        setStartPoint(e.latlng);
        
        // Create initial rectangle (will be updated on mousemove)
        const rect = L.rectangle([e.latlng, e.latlng], {
          color: '#B1336E',
          weight: 2,
          fillOpacity: 0.2,
        }).addTo(rectangleLayerRef.current);
        
        setCurrentRectangle(rect);
      };

      // Mouse move - update rectangle
      const onMouseMove = (e) => {
        if (isDrawing && startPoint && currentRectangle) {
          // Update rectangle bounds
          currentRectangle.setBounds([startPoint, e.latlng]);
        }
      };

      // Mouse up - finish drawing (LEFT CLICK ONLY)
      const onMouseUp = (e) => {
        // Only respond to left click (button 0)
        if (e.originalEvent.button !== 0) return;
        
        if (isDrawing && startPoint && currentRectangle) {
          e.originalEvent.preventDefault(); // Prevent default behavior
          setIsDrawing(false);
          
          // Get final bounds
          const bounds = L.latLngBounds(startPoint, e.latlng);
          
          // Find markers within bounds
          const selectedIds = [];
          
          if (showDeliveryMarkers) {
            const selectableDeliveryPoints = deliveryPoints.filter((dp) =>
              matchesDeliveryMarkerFilter(dp, deliveryMarkerFilter)
            );

            selectableDeliveryPoints.forEach((dp) => {
              if (dp.latitude && dp.longitude) {
                const markerLatLng = L.latLng(dp.latitude, dp.longitude);
                if (bounds.contains(markerLatLng)) {
                  selectedIds.push(dp.id);
                }
              }
            });
          } else if (showOrderMarkers) {
            orders.forEach((order) => {
              if (order.address?.latitude && order.address?.longitude) {
                const markerLatLng = L.latLng(order.address.latitude, order.address.longitude);
                if (bounds.contains(markerLatLng)) {
                  selectedIds.push(order.id);
                }
              }
            });
          }
          
          // Clear the rectangle
          rectangleLayerRef.current.clearLayers();
          setCurrentRectangle(null);
          setStartPoint(null);
          
          // Call callback with selected IDs
          if (selectedIds.length > 0) {
            onDrawingComplete(selectedIds);
          } else {
            alert('No se encontraron puntos dentro del área seleccionada');
          }
        }
      };

      map.on('mousedown', onMouseDown);
      map.on('mousemove', onMouseMove);
      map.on('mouseup', onMouseUp);

      // Cleanup
      return () => {
        map.off('mousedown', onMouseDown);
        map.off('mousemove', onMouseMove);
        map.off('mouseup', onMouseUp);
        map.getContainer().style.cursor = '';
        map.getContainer().removeEventListener('contextmenu', preventContextMenu);
        
        // Re-enable map dragging
        map.dragging.enable();
      };
    } else {
      // Not in drawing mode - restore normal cursor and dragging
      map.getContainer().style.cursor = '';
      map.dragging.enable();
      
      // Clear any active drawing
      if (rectangleLayerRef.current) {
        rectangleLayerRef.current.clearLayers();
      }
      setIsDrawing(false);
      setStartPoint(null);
      setCurrentRectangle(null);
    }
  }, [isDrawingMode, showDeliveryMarkers, showOrderMarkers, deliveryPoints, orders, onDrawingComplete, isDrawing, startPoint, currentRectangle, deliveryMarkerFilter]);
  
  // Render delivery points markers
  useEffect(() => {
    if (!mapInstanceRef.current || !markersLayerRef.current) {
      console.log('⚠️ Map or markers layer not ready yet');
      return;
    }

    if (!showDeliveryMarkers) {
      if (!showOrderMarkers) {
        markersLayerRef.current.clearLayers();
      }
      console.log('  ⏭️ Skipping delivery markers (not active view)');
      return;
    }

    markersLayerRef.current.clearLayers();

    if (showDeliveryMarkers) {
      let renderedCount = 0;
      let skippedCount = 0;
      let districtCount = 0;

      const visibleDeliveryPoints = deliveryPoints.filter((dp) =>
        matchesDeliveryMarkerFilter(dp, deliveryMarkerFilter)
      );

      visibleDeliveryPoints.forEach((dp) => {
        console.log(`  Processing DP ${dp.id}:`, {
          lat: dp.latitude,
          lng: dp.longitude,
          district: dp.district
        });

        let lat, lng;
        let shouldBounce = false;

        if (dp.latitude && dp.longitude) {
          lat = dp.latitude;
          lng = dp.longitude;
          console.log(`    ✅ Using main coordinates: ${lat}, ${lng}`);
        } else if (dp.district) {
          const districtCoords = districts[dp.district];
          if (districtCoords) {
            const offset = 0.001;
            lat = districtCoords.latitude + (Math.random() - 4.5) * offset;
            lng = districtCoords.longitude + (Math.random() - 4.5) * offset;
            shouldBounce = true;
            districtCount++;
            console.log(`    ⚠️ Using district coordinates (${dp.district}): ${lat}, ${lng}`);
          } else {
            console.log(`    ❌ District "${dp.district}" not found in districts list`);
            skippedCount++;
            return;
          }
        } else {
          console.log(`    ❌ Skipping - no coordinates and no district`);
          skippedCount++;
          return;
        }

        // Display text logic
        let displayText;
        let fontSize = '10px';
        
        if (dp.driver_id && dp.sequence) {
          displayText = `${dp.driver_id}-${dp.sequence}`;
          fontSize = '8px';
        } else if (dp.sequence) {
          displayText = `${dp.sequence}`;
          fontSize = '10px';
        } else {
          displayText = String(dp.id).slice(-3);
          fontSize = '9px';
        }

        // Determine color based on status and driver
        let statusColor;
        if (dp.status === 'pending' && dp.driver_id) {
          // Use driver color for pending deliveries with assigned driver
          statusColor = getDriverColor(dp.driver_id);
        } else {
          // Use status color for all other cases
          statusColor = getDeliveryStatusColor(dp.status);
        }

        const slotColor = getSlotColor(dp.slot);
        const slotLabel = getDeliverySlotLabel(dp.slot);
        const pricing = getDeliveryPricingInfo(dp);
        const specialBorder = pricing.isSpecialTariff ? '#F59E0B' : 'white';
        const specialRing = pricing.isSpecialTariff
          ? 'box-shadow: 0 0 0 5px rgba(245, 158, 11, 0.22), 0 3px 10px rgba(120, 53, 15, 0.35);'
          : 'box-shadow: 0 2px 6px rgba(0,0,0,0.3);';
        const specialBadge = pricing.isSpecialTariff ? `
          <div style="
            position: absolute;
            top: -8px;
            left: -8px;
            min-width: 18px;
            height: 18px;
            padding: 0 4px;
            border-radius: 999px;
            background: linear-gradient(135deg, #FBBF24 0%, #D97706 100%);
            color: #111827;
            border: 2px solid white;
            box-shadow: 0 2px 5px rgba(120,53,15,0.35);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            font-weight: 900;
            font-family: 'Montserrat', sans-serif;
          ">S/</div>
        ` : '';
        const addonBadge = pricing.hasAddons ? `
          <div style="
            position: absolute;
            bottom: -7px;
            left: 50%;
            transform: translateX(-50%);
            border-radius: 999px;
            background: #7C3AED;
            color: white;
            border: 2px solid white;
            padding: 1px 5px;
            font-size: 8px;
            font-weight: 900;
            font-family: 'Montserrat', sans-serif;
            box-shadow: 0 2px 5px rgba(76,29,149,0.3);
          ">+${pricing.addonCount}</div>
        ` : '';

        const icon = L.divIcon({
          className: 'custom-delivery-marker',
          html: `
            <div style="position: relative; display: inline-block;">
              <div style="
                width: 32px;
                height: 32px;
                border-radius: 50%;
                background-color: ${statusColor};
                border: 3px solid ${specialBorder};
                ${specialRing}
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: ${fontSize};
                font-weight: bold;
                color: black;
                font-family: 'Montserrat', sans-serif;
                line-height: 1;
                ${shouldBounce ? 'animation: bounce 2s ease-in-out infinite;' : ''}
              ">
                ${displayText}
              </div>
              ${specialBadge}
              ${dp.slot ? `
                <div style="
                  position: absolute;
                  top: -6px;
                  right: -6px;
                  width: 14px;
                  height: 14px;
                  border-radius: 50%;
                  background-color: ${slotColor};
                  border: 2px solid white;
                  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
                "></div>
              ` : ''}
              ${addonBadge}
            </div>
          `,
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        });

        const marker = L.marker([lat, lng], { icon });
        marker.on('click', () => onMarkerClick(dp.id, 'delivery'));
        marker.bindPopup(`
          <div style="font-family: 'Montserrat', sans-serif;">
            <strong>#${dp.id}</strong><br/>
            ${dp.recipient_name}<br/>
            <span style="color: #666;">${dp.status}</span>
            ${dp.driver_id ? `<br/><small>Motorizado: ${dp.driver_id}</small>` : ''}
            ${dp.sequence ? `<br/><small>Seq: ${dp.sequence}</small>` : ''}
            ${slotLabel ? `<br/><small>Horario: ${slotLabel}</small>` : ''}
            ${pricing.isSpecialTariff ? `<br/><small style="color: #B45309; font-weight: 700;">Tarifa especial: ${pricing.serviceLabel} ${pricing.totalLabel}</small>` : ''}
            ${pricing.addonLabels.length ? `<br/><small style="color: #6D28D9;">Addons: ${pricing.addonLabels.join(', ')}</small>` : ''}
            ${shouldBounce ? `<br/><small style="color: #F59E0B;">⚠️ Ubicación aproximada (${dp.district})</small>` : ''}
            <br/>
            <button 
              onclick="window.dispatchEvent(new CustomEvent('openDetailModal', { detail: { id: ${dp.id}, type: 'delivery' } }))"
              style="
                margin-top: 8px;
                background-color: #B1336E;
                color: white;
                border: none;
                padding: 4px 12px;
                border-radius: 4px;
                cursor: pointer;
                font-size: 12px;
                font-weight: 500;
              "
            >
              Ver detalles
            </button>
          </div>
        `);
        marker.addTo(markersLayerRef.current);
        renderedCount++;
        console.log(`    ✅ Marker rendered for DP ${dp.id}`);
      });

      console.log(`📍 Delivery markers summary:`);
      console.log(`  ✅ Rendered: ${renderedCount}`);
      console.log(`  ⚠️ Using district coords: ${districtCount}`);
      console.log(`  ❌ Skipped: ${skippedCount}`);
    }
  }, [deliveryPoints, showDeliveryMarkers, showOrderMarkers, onMarkerClick, deliveryMarkerFilter]);

  // Render orders markers - ✨ UPDATED WITH TIME SLOT BADGES
  useEffect(() => {
    if (!mapInstanceRef.current || !markersLayerRef.current) {
      console.log('⚠️ Map or markers layer not ready yet');
      return;
    }

    console.log('🔄 Rendering order markers...');
    console.log('  showOrderMarkers:', showOrderMarkers);
    console.log('  orders:', orders.length);

    if (showOrderMarkers) {
      markersLayerRef.current.clearLayers();
    }

    if (showOrderMarkers) {
      let renderedCount = 0;
      let skippedCount = 0;

      orders.forEach((order) => {
        console.log(`  Processing Order ${order.id}:`, order.address);

        if (!order.address?.latitude || !order.address?.longitude) {
          console.log(`    ❌ Skipping - no address coordinates`);
          skippedCount++;
          return;
        }

        // Display text logic
        let displayText;
        let fontSize = '10px';
        
        if (order.driver_id && order.sequence) {
          displayText = `${order.driver_id}-${order.sequence}`;
          fontSize = '8px';
        } else if (order.sequence) {
          displayText = `${order.sequence}`;
          fontSize = '10px';
        } else {
          displayText = String(order.id).slice(-3);
          fontSize = '9px';
        }

        // Determine color based on status and driver
        let statusColor;
        if (order.status === 'pending' && order.driver_id) {
          // Use driver color for pending orders with assigned driver
          statusColor = getDriverColor(order.driver_id);
          console.log(`    🎨 Using driver color for pending Order ${order.id}, driver ${order.driver_id}: ${statusColor}`);
        } else {
          // Use status color for all other cases
          statusColor = getOrderStatusColor(order.status);
        }

        // ✨ NEW: Time slot indicator
        let timeSlotBadge = '';
        let timeSlotEmoji = '';
        let timeSlotLabel = '';
        
        if (order.pickup_time_slot) {
          if (order.pickup_time_slot === 'morning') {
            timeSlotEmoji = '☀️';
            timeSlotLabel = 'Mañana (6-9am)';
            timeSlotBadge = `
              <div style="
                position: absolute;
                top: -4px;
                right: -4px;
                width: 16px;
                height: 16px;
                background: linear-gradient(135deg, #FCD34D 0%, #F59E0B 100%);
                border: 2px solid white;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 9px;
                box-shadow: 0 1px 3px rgba(0,0,0,0.3);
              ">
                ☀️
              </div>
            `;
          } else if (order.pickup_time_slot === 'afternoon') {
            timeSlotEmoji = '🌙';
            timeSlotLabel = 'Tarde (11am-1pm)';
            timeSlotBadge = `
              <div style="
                position: absolute;
                top: -4px;
                right: -4px;
                width: 16px;
                height: 16px;
                background: linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%);
                border: 2px solid white;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 9px;
                box-shadow: 0 1px 3px rgba(0,0,0,0.3);
              ">
                🌙
              </div>
            `;
          }
          console.log(`    ⏰ Time slot: ${order.pickup_time_slot} (${timeSlotLabel})`);
        }

        const icon = L.divIcon({
          className: 'custom-order-marker',
          html: `
            <div style="position: relative;">
              <div style="
                width: 32px;
                height: 32px;
                background-color: ${statusColor};
                border: 3px solid white;
                box-shadow: 0 2px 6px rgba(0,0,0,0.3);
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: ${fontSize};
                font-weight: bold;
                color: black;
                font-family: 'Montserrat', sans-serif;
                line-height: 1;
                border-radius: 50%;
              ">
                ${displayText}
              </div>
              ${timeSlotBadge}
            </div>
          `,
          iconSize: [32, 32],
          iconAnchor: [16, 16],
        });

        const marker = L.marker([order.address.latitude, order.address.longitude], { icon });
        marker.on('click', () => onMarkerClick(order.id, 'order'));
        
        // Enhanced popup with time slot info
        const timeSlotInfo = order.pickup_time_slot 
          ? `<br/><small style="color: ${order.pickup_time_slot === 'morning' ? '#F59E0B' : '#7C3AED'};">
               ${timeSlotEmoji} ${timeSlotLabel}
             </small>`
          : '';
        
        marker.bindPopup(`
          <div style="font-family: 'Montserrat', sans-serif;">
            <strong>#${order.id}</strong><br/>
            ${order.customer_name}<br/>
            <span style="color: #666;">${order.status}</span>
            ${order.driver_id ? `<br/><small>Motorizado: ${order.driver_id}</small>` : ''}
            ${order.sequence ? `<br/><small>Seq: ${order.sequence}</small>` : ''}
            ${timeSlotInfo}
            <br/>
            <button 
              onclick="window.dispatchEvent(new CustomEvent('openDetailModal', { detail: { id: ${order.id}, type: 'order' } }))"
              style="
                margin-top: 8px;
                background-color: #B1336E;
                color: white;
                border: none;
                padding: 4px 12px;
                border-radius: 4px;
                cursor: pointer;
                font-size: 12px;
                font-weight: 500;
              "
            >
              Ver detalles
            </button>
          </div>
        `);
        marker.addTo(markersLayerRef.current);
        renderedCount++;
        console.log(`    ✅ Marker rendered for Order ${order.id}`);
      });

      console.log(`📦 Order markers summary:`);
      console.log(`  ✅ Rendered: ${renderedCount}`);
      console.log(`  ❌ Skipped: ${skippedCount}`);
    } else {
      console.log('  ⏭️ Skipping order markers (not active view)');
    }
  }, [orders, showOrderMarkers, onMarkerClick]);

  // Render driver markers (always visible)
  useEffect(() => {
    if (!mapInstanceRef.current || !driversLayerRef.current) return;

    console.log('🚗 Rendering driver markers...');
    console.log('  drivers:', drivers.length);

    driversLayerRef.current.clearLayers();

    let renderedCount = 0;
    drivers.forEach((driver) => {
      if (!driver.latitude || !driver.longitude) {
        console.log(`  ❌ Driver ${driver.id} has no coordinates`);
        return;
      }

      const driverName = String(driver.name || '').trim();
      const shortName = Array.from(driverName).slice(0, 5).join('');
      const safeName = escapeMarkerText(driverName);
      const safeShortName = escapeMarkerText(shortName);
      const safeId = escapeMarkerText(driver.id);
      const isSelected = selectedDriverId != null && String(selectedDriverId) === String(driver.id);
      const markerColor = isSelected ? '#B1336E' : getDriverColor(driver.id);
      const markerLabel = safeShortName ? `${safeShortName} #${safeId}` : `#${safeId}`;

      const icon = L.divIcon({
        className: 'custom-driver-marker',
        html: `
          <div style="
            position: relative;
            width: 40px;
            height: 40px;
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <span style="
              position: absolute;
              bottom: 38px;
              left: 50%;
              transform: translateX(-50%);
              width: max-content;
              max-width: 70px;
              box-sizing: border-box;
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
              border: 1px solid ${markerColor};
              border-radius: 999px;
              background: ${isSelected ? '#B1336E' : 'white'};
              color: ${isSelected ? 'white' : '#1e293b'};
              box-shadow: 0 1px 4px rgba(0,0,0,0.24);
              padding: 2px 4px;
              font: 700 9px/12px Montserrat, sans-serif;
              pointer-events: none;
            ">${markerLabel}</span>
            <img
              src="${process.env.PUBLIC_URL}/logo.png"
              alt="Conductor ${safeName || safeId}"
              style="
                width: 40px;
                height: 40px;
                object-fit: contain;
                filter: ${isSelected
                  ? 'drop-shadow(0 0 4px rgba(177,51,110,0.85))'
                  : 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'};
              "
            />
          </div>
        `,
        iconSize: [40, 40],
        iconAnchor: [20, 20],
      });

      const marker = L.marker([driver.latitude, driver.longitude], {
        icon,
        zIndexOffset: isSelected ? 1000 : 0,
      });
      marker.bindPopup(`
        <div style="font-family: 'Montserrat', sans-serif;">
          <strong>${safeName ? `${safeName} #${safeId}` : `Conductor #${safeId}`}</strong><br/>
          <span style="color: #E6B43C; font-weight: bold;">En línea</span>
        </div>
      `);
      marker.addTo(driversLayerRef.current);
      renderedCount++;
    });

    console.log(`  ✅ Rendered ${renderedCount} driver markers`);
  }, [drivers, selectedDriverId]);

  return (
    <>
      <div ref={mapRef} className="w-full h-full" />
      <style>{`
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
      `}</style>
    </>
  );
}

// Helper functions for colors
function getDeliveryStatusColor(status) {
  const colors = {
    pending: '#F59E0B',
    encamino: '#3B82F6',
    entregado: '#10B981',
    cancelado: '#6B7280',
    rechazado: '#EF4444',
    postergado: '#F97316',
  };
  return colors[status] || '#94A3B8';
}

function getOrderStatusColor(status) {
  const colors = {
    pending: '#F59E0B',
    encamino: '#3B82F6',
    recogido: '#10B981',
    cancelado: '#6B7280',
  };
  return colors[status] || '#94A3B8';
}

function getSlotColor(slot) {
  const colors = {
    1: '#DC2626', // Intense red
    2: '#F97316', // Orange
    3: '#FBBF24', // Yellow
    4: '#BEF264', // Light yellow-green
    5: '#FEF08A', // Very light yellow
    6: '#A78BFA', // Violet
  };
  return colors[slot] || '#94A3B8';
}

function getDriverColor(driverId) {
  const colors = {
    3: '#ADD8E6',      // lightblue
    183: '#90EE90',    // lightgreen
    6: '#FFB6C1',      // lightpink
    7: '#FFDAB9',      // peachpuff
    8: '#F08080',      // lightcoral
    54: '#FAFAD2',     // lightgoldenrodyellow
    191: '#DDA0DD',    // plum
    164: '#66CDAA',    // mediumaquamarine
    119: '#FFA07A',    // lightsalmon
    4: '#20B2AA',      // lightseagreen
    240: '#E0FFFF',    // lightcyan
  };
  return colors[driverId] || '#94A3B8'; // Default gray
}

export default React.memo(ControlTowerMap);
